import { appFetch } from "../../utils/appFetch";

export interface AppFetchResponse {
    data: any;
    status: number;
}

function unwrap<T>(res: AppFetchResponse): { ok: boolean; data?: T; error?: string } {
    const ok = res.status >= 200 && res.status < 300;
    const payload = res?.data;
    return { ok, data: payload as T, error: (payload as any)?.error || (payload as any)?.reason };
}

export type KnowledgeBaseSourceType = "faq" | "policy" | "brand" | "custom";
export type KnowledgeBaseStatus = "draft" | "active" | "archived";

export interface KnowledgeBaseDocumentDto {
    id: number;
    shopId: number;
    sourceType: KnowledgeBaseSourceType;
    status: KnowledgeBaseStatus;
    title: string;
    language: string;
    sourceKey?: string | null;
    contentText: string;
    metadataJson?: any | null;
    createdAt: string;
    updatedAt: string;
    chunksCount?: number;
}

export interface KnowledgeBaseChunkDto {
    id: number;
    shopId: number;
    documentId: number;
    chunkIndex: number;
    contentText: string;
    metadataJson?: any | null;
    createdAt: string;
    updatedAt: string;
}

export interface UpsertKnowledgeBaseDocumentBody {
    sourceType: KnowledgeBaseSourceType;
    status: KnowledgeBaseStatus;
    title: string;
    language: string;
    sourceKey?: string | null;
    contentText: string;
    metadataJson?: any | null;
}

export const ListKnowledgeBaseDocuments = async (domain: string) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: "v2/knowledge-base",
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });
    return unwrap<{ ok: boolean; documents: KnowledgeBaseDocumentDto[] }>(res);
};

export const GetKnowledgeBaseDocument = async (domain: string, documentId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/knowledge-base/${documentId}`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });
    return unwrap<{ ok: boolean; document: KnowledgeBaseDocumentDto; chunks: KnowledgeBaseChunkDto[] }>(res);
};

export const CreateKnowledgeBaseDocument = async (domain: string, body: UpsertKnowledgeBaseDocumentBody) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: "v2/knowledge-base",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: body,
    });
    return unwrap<{ ok: boolean; id: number; document: KnowledgeBaseDocumentDto; chunks: KnowledgeBaseChunkDto[] }>(res);
};

export const UpdateKnowledgeBaseDocument = async (domain: string, documentId: number, body: UpsertKnowledgeBaseDocumentBody) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/knowledge-base/${documentId}`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: body,
    });
    return unwrap<{ ok: boolean; id: number; document: KnowledgeBaseDocumentDto; chunks: KnowledgeBaseChunkDto[] }>(res);
};

export const DeleteKnowledgeBaseDocument = async (domain: string, documentId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/knowledge-base/${documentId}`,
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        domain,
    });
    return unwrap<{ ok: boolean; id: number }>(res);
};
